import { useState } from 'react';
import { toast } from 'sonner';
import { featureIdeasApi } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2 } from 'lucide-react';

/**
 * Sidebar box (replaces the old permanent "Your data is locked" panel —
 * that reassurance still lives on /privacy): a lightweight way to say what
 * you wish the tool could do. Submissions are matched against existing
 * ideas server-side (AI, with a conservative deterministic fallback) so
 * near-duplicates add a vote instead of piling up — see
 * backend/app/services/feature_ideas.py. This is a suggestion box, not a
 * public roadmap: nobody sees anyone else's ideas or vote counts here,
 * only admins do.
 */
export const FeatureIdeaBox = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < 3) return;
    setSending(true);
    try {
      const { data } = await featureIdeasApi.submit(trimmed);
      toast.success(data?.message || 'Thanks for the idea!');
      setOpen(false);
      setText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send that. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="mt-3 rounded-[12px] bg-inset p-4">
        <p className="text-[15px] font-semibold text-ink">Got an idea?</p>
        <p className="mt-1 text-[13px] leading-snug text-ink-55">
          Tell us what you wish this could do.
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-2 inline-block text-[13px] font-semibold text-brand hover:underline"
          data-testid="feature-idea-open-btn"
        >
          Suggest a feature
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suggest a feature</DialogTitle>
            <DialogDescription>
              What do you wish this could do? Short or long, doesn't matter — if someone's
              already suggested something similar, we'll add your vote to it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="feature-idea-text" className="sr-only">Your idea</label>
            <Textarea
              id="feature-idea-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="For example: let me export a single organisation's timeline as a PDF."
              className="bg-surface border-surface"
              data-testid="feature-idea-text"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button onClick={submit} disabled={sending || text.trim().length < 3} data-testid="feature-idea-submit">
              {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeatureIdeaBox;
